import React, { useState } from "react";
import { Helmet } from "react-helmet";
import { Link, useNavigate } from "react-router-dom";
import { 
  Bot, CheckCircle2, ArrowRight, ArrowLeft, Loader2, Globe, Search, 
  Users, Zap, TrendingUp, Shield, BarChart2, Mic, MessageSquare, Eye
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import axios from "axios";
import { API, getContentSource } from "@/lib/constants";

const STATS = [
  { value: "84%", label: "of queries now show AI Overviews", icon: Bot },
  { value: "40%", label: "of Gen Z prefer AI search over Google", icon: MessageSquare },
  { value: "3x", label: "higher CTR from AI citations vs. page-2 rankings", icon: TrendingUp },
];

const AUDIT_COVERS = [
  { icon: Search, title: "Traditional SEO Health", desc: "Technical SEO, on-page signals, backlink profile, Core Web Vitals" },
  { icon: Bot, title: "AI Citation Frequency", desc: "How often ChatGPT, Perplexity, and Gemini mention your brand" },
  { icon: Globe, title: "Schema & Structured Data", desc: "Organization, FAQ, Service, Article schema completeness" },
  { icon: Mic, title: "Voice Search Readiness", desc: "Featured snippet eligibility, speakable content, FAQ optimization" },
  { icon: Eye, title: "Google AI Overview Presence", desc: "Are you appearing in AI Overviews for your money keywords?" },
  { icon: Shield, title: "Competitor AI Gap Analysis", desc: "Where competitors get cited and you don't — actionable gap report" },
];

const STEPS = [
  { id: "site", label: "Your Site" },
  { id: "goals", label: "Your Goals" },
  { id: "contact", label: "Contact Info" },
];

const FreeAIVisibilityAuditPage = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [form, setForm] = useState({
    website_url: "", target_keywords: "", competitors: "",
    city: "", goal: "", current_traffic: "",
    first_name: "", last_name: "", email: "", phone: "",
    business_name: ""
  });

  const update = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

  const canAdvance = () => {
    if (step === 0) return form.website_url;
    if (step === 1) return form.goal;
    if (step === 2) return form.first_name && form.email;
    return true;
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      await axios.post(`${API}/leads/service-inquiry`, {
        first_name: form.first_name,
        last_name: form.last_name,
        email: form.email,
        phone: form.phone,
        website_url: form.website_url,
        business_type: form.business_name || "Not specified",
        industry: "Not specified",
        business_size: "Not specified",
        worked_with_agency: "Not specified",
        seo_understanding: "Not specified",
        business_scope: form.city || "Not specified",
        crm_tools: "",
        budget: "Free AI Visibility Audit",
        timeline: "ASAP",
        additional_notes: `Goal: ${form.goal}\nKeywords: ${form.target_keywords || "N/A"}\nCompetitors: ${form.competitors || "N/A"}\nCity: ${form.city || "N/A"}\nTraffic: ${form.current_traffic || "N/A"}`,
        service: "Free AI Visibility Audit"
      });
      navigate("/thank-you/lead?from=audit");
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const RadioOption = ({ field, value, label }) => (
    <button
      type="button"
      onClick={() => update(field, value)}
      data-testid={`audit-option-${value.toLowerCase().replace(/\s+/g, '-').slice(0, 30)}`}
      className={`w-full text-left px-4 py-3 rounded-lg border transition-all text-sm ${
        form[field] === value
          ? "border-[#2ABFBF] bg-[#2ABFBF]/10 text-white"
          : "border-white/10 bg-white/5 text-gray-400 hover:border-white/20 hover:bg-white/10"
      }`}
    >
      {label}
    </button>
  );

  return (
    <div className="min-h-screen pt-28 pb-20" data-testid="free-audit-page">
      <Helmet>
        <title>Free AI Visibility Audit | See How AI Search Engines See You | Digital Media Ninja</title>
        <meta name="description" content="Get a free AI visibility audit. We'll check your SEO health, AI citation frequency, schema markup, and voice search readiness. Custom report in 24 hours." />
        <link rel="canonical" href="https://digitalmedianinja.com/free-ai-visibility-audit/" />
      </Helmet>

      {/* Hero */}
      <section className="container-custom max-w-6xl mx-auto px-4">
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#2ABFBF]/10 border border-[#2ABFBF]/20 text-[#2ABFBF] text-sm font-medium mb-6">
            <Bot className="w-4 h-4" /> Free — No Credit Card Required
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
            Free AI Visibility Audit
          </h1>
          <p className="text-lg text-gray-400 max-w-2xl mx-auto">
            Find out if ChatGPT, Perplexity, and Google AI Overviews are citing your business — or sending customers to your competitors.
          </p>
        </div>

        {/* Stats Bar */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-16">
          {STATS.map((stat, i) => (
            <div key={i} className="bg-white/5 border border-white/10 rounded-xl p-5 text-center" data-testid={`stat-${i}`}>
              <stat.icon className="w-6 h-6 text-[#2ABFBF] mx-auto mb-3" />
              <div className="text-3xl font-bold text-white mb-1">{stat.value}</div>
              <div className="text-sm text-gray-400">{stat.label}</div>
            </div>
          ))}
        </div>

        {/* Two Column: Form + What You Get */}
        <div className="grid lg:grid-cols-2 gap-12 items-start">
          {/* Left: Form */}
          <div className="bg-[#111] border border-white/10 rounded-2xl overflow-hidden" data-testid="audit-form-card">
            {submitted ? (
              <div className="p-8 text-center">
                <div className="w-16 h-16 bg-[#2ABFBF]/20 rounded-full flex items-center justify-center mx-auto mb-6">
                  <CheckCircle2 className="w-8 h-8 text-[#2ABFBF]" />
                </div>
                <h2 className="text-2xl font-bold text-white mb-3" data-testid="audit-page-success">Your AI Visibility Audit Is On the Way</h2>
                <p className="text-gray-400 mb-4">We'll analyze <span className="text-[#2ABFBF]">{form.website_url}</span> across traditional SEO, AI citations, schema health, and voice search — expect your custom report within 1 business day.</p>
                <Link to="/blog"><Button variant="outline" className="border-white/20 text-gray-300">Read Our Blog While You Wait</Button></Link>
              </div>
            ) : (
              <>
                {/* Progress */}
                <div className="px-6 pt-5 pb-3">
                  <div className="flex items-center justify-between mb-3">
                    <h2 className="text-lg font-bold text-white">Get Your Free Report</h2>
                    <span className="text-xs text-gray-500">Step {step + 1} of {STEPS.length}</span>
                  </div>
                  <div className="flex gap-1.5">
                    {STEPS.map((_, i) => (
                      <div key={i} className={`h-1 flex-1 rounded-full transition-colors ${i <= step ? "bg-[#2ABFBF]" : "bg-white/10"}`} />
                    ))}
                  </div>
                </div>

                {/* Step Content */}
                <form
                  id="ai-visibility-audit-form"
                  name="ai-visibility-audit-form"
                  className="px-6 py-4 space-y-4 min-h-[320px]"
                  onSubmit={(e) => { e.preventDefault(); if (step === STEPS.length - 1 && canAdvance() && !submitting) handleSubmit(); }}
                >
                  {step === 0 && (
                    <>
                      <div>
                        <label className="text-gray-400 text-xs font-medium mb-1.5 block">Website URL *</label>
                        <div className="relative">
                          <Globe className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                          <Input name="website_url" value={form.website_url} onChange={e => update("website_url", e.target.value)} placeholder="https://yourwebsite.com" className="bg-white/5 border-white/10 text-white pl-10" data-testid="page-audit-website" />
                        </div>
                      </div>
                      <div>
                        <label className="text-gray-400 text-xs font-medium mb-1.5 block">Target Keywords</label>
                        <div className="relative">
                          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                          <Input name="target_keywords" value={form.target_keywords} onChange={e => update("target_keywords", e.target.value)} placeholder="e.g. plumber dallas, emergency plumbing" className="bg-white/5 border-white/10 text-white pl-10" data-testid="page-audit-keywords" />
                        </div>
                      </div>
                      <div>
                        <label className="text-gray-400 text-xs font-medium mb-1.5 block">Top Competitors</label>
                        <div className="relative">
                          <Users className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                          <Input name="competitors" value={form.competitors} onChange={e => update("competitors", e.target.value)} placeholder="e.g. competitor1.com, competitor2.com" className="bg-white/5 border-white/10 text-white pl-10" data-testid="page-audit-competitors" />
                        </div>
                      </div>
                      <div>
                        <label className="text-gray-400 text-xs font-medium mb-1.5 block">City / Metro Area</label>
                        <Input name="city" value={form.city} onChange={e => update("city", e.target.value)} placeholder="e.g. Dallas, TX" className="bg-white/5 border-white/10 text-white" data-testid="page-audit-city" />
                      </div>
                    </>
                  )}
                  {step === 1 && (
                    <>
                      <div>
                        <label className="text-gray-400 text-xs font-medium mb-2 block">#1 goal for this audit? *</label>
                        <div className="space-y-2">
                          {[
                            "Rank higher on Google",
                            "Get cited by ChatGPT / Perplexity / AI search",
                            "Show up in Google AI Overviews",
                            "Improve local map pack visibility",
                            "Outrank a specific competitor",
                            "I want a full picture of everything"
                          ].map(v => (
                            <RadioOption key={v} field="goal" value={v} label={v} />
                          ))}
                        </div>
                      </div>
                      <div>
                        <label className="text-gray-400 text-xs font-medium mb-2 block">Current monthly organic traffic?</label>
                        <div className="grid grid-cols-2 gap-2">
                          {["Not sure", "Under 1,000", "1,000 - 10,000", "10,000 - 50,000", "50,000+"].map(v => (
                            <RadioOption key={v} field="current_traffic" value={v} label={v} />
                          ))}
                        </div>
                      </div>
                    </>
                  )}
                  {step === 2 && (
                    <>
                      <input type="hidden" name="website_url" value={form.website_url} />
                      <input type="hidden" name="target_keywords" value={form.target_keywords} />
                      <input type="hidden" name="competitors" value={form.competitors} />
                      <input type="hidden" name="city" value={form.city} />
                      <input type="hidden" name="goal" value={form.goal} />
                      <input type="hidden" name="content_source" value={getContentSource()} />
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="text-gray-400 text-xs font-medium mb-1.5 block">First Name *</label>
                          <Input name="first_name" value={form.first_name} onChange={e => update("first_name", e.target.value)} placeholder="John" className="bg-white/5 border-white/10 text-white" data-testid="page-audit-fname" />
                        </div>
                        <div>
                          <label className="text-gray-400 text-xs font-medium mb-1.5 block">Last Name</label>
                          <Input name="last_name" value={form.last_name} onChange={e => update("last_name", e.target.value)} placeholder="Smith" className="bg-white/5 border-white/10 text-white" data-testid="page-audit-lname" />
                        </div>
                      </div>
                      <div>
                        <label className="text-gray-400 text-xs font-medium mb-1.5 block">Business Name</label>
                        <Input name="business_name" value={form.business_name} onChange={e => update("business_name", e.target.value)} placeholder="Your Company LLC" className="bg-white/5 border-white/10 text-white" data-testid="page-audit-business" />
                      </div>
                      <div>
                        <label className="text-gray-400 text-xs font-medium mb-1.5 block">Email *</label>
                        <Input type="email" name="email" value={form.email} onChange={e => update("email", e.target.value)} placeholder="john@company.com" className="bg-white/5 border-white/10 text-white" data-testid="page-audit-email" />
                      </div>
                      <div>
                        <label className="text-gray-400 text-xs font-medium mb-1.5 block">Phone</label>
                        <Input type="tel" name="phone" value={form.phone} onChange={e => update("phone", e.target.value)} placeholder="(214) 555-1234" className="bg-white/5 border-white/10 text-white" data-testid="page-audit-phone" />
                      </div>
                    </>
                  )}
                </form>

                {/* Footer Nav */}
                <div className="px-6 py-4 border-t border-white/10 flex items-center justify-between">
                  {step > 0 ? (
                    <Button type="button" variant="ghost" onClick={() => setStep(step - 1)} className="text-gray-400 hover:text-white">
                      <ArrowLeft className="w-4 h-4 mr-2" /> Back
                    </Button>
                  ) : <div />}
                  {step < STEPS.length - 1 ? (
                    <Button type="button" onClick={() => setStep(step + 1)} disabled={!canAdvance()} className="bg-[#2ABFBF] hover:bg-[#1E9999] text-black font-semibold disabled:opacity-40" data-testid="page-audit-next">
                      Next <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  ) : (
                    <Button type="submit" form="ai-visibility-audit-form" disabled={!canAdvance() || submitting} className="bg-[#2ABFBF] hover:bg-[#1E9999] text-black font-semibold disabled:opacity-40" data-testid="page-audit-submit">
                      {submitting ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Submitting...</> : "Get My Free Report"}
                    </Button>
                  )}
                </div>
              </>
            )}
          </div>

          {/* Right: What the Audit Covers */}
          <div>
            <h2 className="text-2xl font-bold text-white mb-6">What Your Audit Covers</h2>
            <div className="space-y-4">
              {AUDIT_COVERS.map((item, i) => (
                <div key={i} className="flex items-start gap-4 bg-white/5 border border-white/10 rounded-xl p-4" data-testid={`audit-cover-${i}`}>
                  <div className="w-10 h-10 rounded-lg bg-[#2ABFBF]/10 flex items-center justify-center shrink-0">
                    <item.icon className="w-5 h-5 text-[#2ABFBF]" />
                  </div>
                  <div>
                    <h3 className="text-white font-semibold text-sm mb-1">{item.title}</h3>
                    <p className="text-gray-400 text-sm">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>

            {/* Social Proof */}
            <div className="mt-8 bg-gradient-to-r from-[#2ABFBF]/10 to-transparent border border-[#2ABFBF]/20 rounded-xl p-5">
              <div className="flex items-center gap-2 mb-3">
                {[1,2,3,4,5].map(i => <span key={i} className="text-yellow-400 text-lg">&#9733;</span>)}
              </div>
              <p className="text-gray-300 text-sm italic mb-2">"DMN's audit showed us we were invisible to AI search engines. Within 3 months of implementing their GEO strategy, we went from zero AI citations to being recommended in 4 out of 5 ChatGPT queries for our niche."</p>
              <p className="text-gray-500 text-xs">— Texas-based HVAC company, 2026 client</p>
            </div>
          </div>
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="container-custom max-w-4xl mx-auto px-4 mt-20 text-center">
        <div className="bg-gradient-to-br from-[#2ABFBF]/10 to-[#111] border border-[#2ABFBF]/20 rounded-2xl p-10">
          <h2 className="text-2xl font-bold text-white mb-3">Not ready for the full audit?</h2>
          <p className="text-gray-400 mb-6">Check out our GEO and AEO service pages to learn how AI search optimization works.</p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Link to="/services/geo"><Button className="bg-[#2ABFBF] hover:bg-[#1E9999] text-black font-semibold">Learn About GEO</Button></Link>
            <Link to="/services/aeo"><Button variant="outline" className="border-white/20 text-gray-300 hover:bg-white/5">Learn About AEO</Button></Link>
            <Link to="/services/llm-optimization"><Button variant="outline" className="border-white/20 text-gray-300 hover:bg-white/5">LLM Optimization</Button></Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default FreeAIVisibilityAuditPage;
